import React from 'react';
import {useTranslation} from 'react-i18next';
import {ExecutionDetailsService} from '@store/operations/executions';
import ModalComponent from '@components/modal/Modal.component';
import NewTableComponent from '@components/table/NewTable.component';
import {GridCellParams, GridValueGetterParams} from '@mui/x-data-grid-pro';
import {Stack} from '@mui/material';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {ClientsService, clientsServicesQuery, clientsServicesService} from '@store/clients/services';

interface SelectServiceModalProps {
  services: ExecutionDetailsService[];
  handleClose: () => void;
}

const SelectServiceModal = (props: SelectServiceModalProps) => {
  const {services, handleClose} = props;
  const {t} = useTranslation();

  return (
    <ModalComponent
      title={t('operations.executions.linkedExecutionModal.title')}
      aria-label="over-drawer"
      handleClose={handleClose}
      maxWidth={false}
      content={
        <Stack>
          <NewTableComponent
            columns={[
              COLUMN_TYPES.TEXT_BOLD({field: 'id', label: t('clients.servicesTable.id'), width: 100}),
              COLUMN_TYPES.TEXT_BOLD({
                field: 'clientOrganisationUnitName',
                label: t('clients.servicesTable.clientUnitName')
              }),
              COLUMN_TYPES.TEXT_BOLD({
                field: 'service.clientWasteName',
                label: t('clients.servicesTable.name'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.clientWasteName,
                fullWidth: true,
                minWidth: 350,
              }),
              COLUMN_TYPES.TEXT({
                field: 'schedulesCount',
                label: t('clients.servicesTable.frequency'),
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => (params.value ?? 0) > 0 ? t('global.frequencies.recurrent') : t('global.frequencies.onDemand'),
                width: 130,
              }),
              COLUMN_TYPES.TEXT({
                field: 'kind.kind',
                label: t('clients.servicesTable.type'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.kindName,
                width: 250,
              }),
              COLUMN_TYPES.TEXT({
                field: 'waste.name',
                label: t('clients.servicesTable.details'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.wasteName,
                width: 250,
              }),
              COLUMN_TYPES.TEXT({
                field: 'container.name',
                label: t('clients.servicesTable.container'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.containerName,
                width: 200,
              }),
              COLUMN_TYPES.TEXT({
                field: 'sOrg.name',
                label: t('clients.servicesTable.supplier'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.supplierOrganisationName,
                fullWidth: true,
                minWidth: 350,
              }),
              COLUMN_TYPES.TEXT({
                field: 'buyPriceUnit',
                label: t('clients.servicesTable.purchasePrice'),
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.buyPriceIfOnlyOne !== null
                  ? `${params.row.buyPriceIfOnlyOne / 100} ${params.row.buyPriceCurrency || ''}`
                  : `${params.row.buyPricesCount || '-'}${params.row.buyPricesCount ? ' ' + t('suppliers.details.contractedServices.table.prices') : ''}`,
                width: 130,
              }),
              COLUMN_TYPES.TEXT({
                field: 'sellPriceUnit',
                label: t('clients.servicesTable.sellingPrice'),
                cellClassName: (params: GridCellParams<ClientsService>) => params.row.buyPriceIfOnlyOne && params.row.sellPriceIfOnlyOne && params.row.buyPriceIfOnlyOne > params.row.sellPriceIfOnlyOne ? 'error' : '',
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.sellPriceIfOnlyOne !== null
                  ? `${params.row.sellPriceIfOnlyOne / 100} ${params.row.sellPriceCurrency || ''}`
                  : `${params.row.sellPricesCount || '-'}${params.row.sellPricesCount ? ' ' + t('suppliers.details.contractedServices.table.prices') : ''}`,
                width: 130,
              }),
            ]}
            handleSortModelChange={clientsServicesService.setSort}
            disableRowSelection={false}
            totalRows={clientsServicesQuery.totalRows}
            data={services}/>
        </Stack>
      }/>

  );
};

export default SelectServiceModal;
